"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Cookie, ShieldCheck, X } from "lucide-react";

const COOKIE_CONSENT_STORAGE_KEY = "G360-cookie-consent-v1";

export function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    try {
      setIsVisible(
        localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) !== "accepted",
      );
    } catch {
      setIsVisible(true);
    } finally {
      setIsReady(true);
    }
  }, []);

  const acknowledgeConsent = () => {
    try {
      localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, "accepted");
    } catch {
      // Ignore storage failures and still dismiss for the active session.
    }

    setIsVisible(false);
  };

  if (!isReady || !isVisible) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed bottom-4 left-4 z-50 sm:bottom-6 sm:left-6">
      <div className="w-96 max-w-[calc(100vw-2rem)] animate-in fade-in slide-in-from-bottom-6 duration-500 sm:max-w-[calc(100vw-3rem)]">
        <Card className="pointer-events-auto relative overflow-hidden border border-white/50 bg-background/88 py-3 shadow-2xl ring-1 ring-primary/10 backdrop-blur-2xl supports-[backdrop-filter]:bg-background/72">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-12 top-0 size-24 rounded-full bg-chart-2/15 blur-3xl" />
            <div className="absolute bottom-0 right-0 size-32 rounded-full bg-primary/14 blur-3xl" />
            <div className="absolute right-20 top-5 size-12 rounded-full border border-border/60 bg-background/60" />
          </div>

          <CardHeader className="relative gap-2.5">
            <div className="flex items-start gap-3">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/20">
                <Cookie className="size-3.5" />
              </div>

              <div className="flex flex-col gap-1.5">
                <CardTitle className="pr-8 text-pretty text-sm">
                  We use cookies to improve and secure your experience.
                </CardTitle>
              </div>
            </div>

            <CardAction>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={acknowledgeConsent}
                aria-label="Dismiss cookie banner"
                className="size-8 rounded-full"
              >
                <X />
              </Button>
            </CardAction>

            <CardDescription className="text-[11px] leading-4.5 sm:text-xs">
              Essential and analytics cookies help with sign-in, performance,
              and preferences. Your choice is saved in this browser.
            </CardDescription>
          </CardHeader>

          <CardFooter className="relative flex flex-col gap-2 border-t border-border/60 pt-3">
            <p className="text-[11px] text-muted-foreground">
              Read the full details in our cookie policy.
            </p>

            <div className="flex w-full gap-2">
              <Button asChild size="sm" variant="outline" className="flex-1">
                <Link href="/cookie-policy">View policy</Link>
              </Button>

              <Button
                type="button"
                size="sm"
                onClick={acknowledgeConsent}
                className="flex-1"
              >
                <ShieldCheck data-icon="inline-start" />
                Accept cookies
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
